import React from 'react';
import { Button, Collapse, TextField, IconButton, Tooltip } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CheckIcon from '@material-ui/icons/Check';
import InvoiceIcon from '@material-ui/icons/Receipt';
import RefreshIcon from '@material-ui/icons/Refresh';
import InboxIcon from '@material-ui/icons/Inbox';
import AuthUtil from '../../../redux/AuthUtil';
import { ONBOARDING } from '../../global/Server/endpoints';
import {
  PLANS,
  COMP_TYPE,
  ADD_ON_BY_ID,
  DURATION_BY_ID,
  computeAuthorities,
  computePricing,
  computeTrialDates,
  generateLicenseKeyHex,
  inferPlanIdFromName,
} from './wizardConfig';

/**
 * Technician "Onboarding Requests" screen, opened inline from the Manage Company
 * toolbar (like Membership Summary — not a dialog). Lists paid sign-up orders
 * (newest first) and works the lifecycle:
 *   Pending Setup → (Start Setup) → In Progress → (Mark Completed) → Completed
 *
 * "Mark Completed" assembles a CompanyWizardV3-style create payload from the
 * order and calls activateOnboardingRequest (CompanyService.create on the
 * backend). The technician then chooses whether to email the customer their
 * credentials. Status only ever moves forward (enforced server-side).
 */

const STATUS = {
  'Pending Setup': { bg: '#FFF7DD', fg: '#92670A', accent: '#E7B600' },
  'In Progress': { bg: '#E0EAFF', fg: '#1E3FD0', accent: '#1E3FD0' },
  Completed: { bg: '#DCFCE7', fg: '#15803D', accent: '#16A34A' },
};

const CHECKLIST = [
  { key: 'config', label: 'Configure company profile & settings' },
  { key: 'master', label: 'Create Master Admin account' },
  { key: 'lic', label: 'Assign licenses' },
];

class OnboardingRequests extends React.Component {
  state = {
    expanded: null,
    filter: 'Pending Setup', // default chip
    adminUserDraft: {},
    passwordDraft: {},
    passwordMode: {}, // orderNumber -> 'manual' | 'auto'
    usernameStatus: {}, // orderNumber -> { state: 'checking'|'available'|'taken', value }
    activating: null,
    confirmWelcome: null,
    sendingWelcome: null,
    welcomeSent: {},
    invoiceLoading: null,
  };

  setFilter = (filter) => this.setState({ filter });

  _userTimers = {}; // debounce timers per order for the availability check

  // Default Master Admin username derived from the company name —
  // "Red Angel" → "admin@redangel.com". Used when the field is left empty.
  defaultUsername = (req) => {
    const slug = (req.company || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return slug ? `admin@${slug}.com` : '';
  };

  // Current username / password values for a request (typed draft → saved value
  // → derived default). These become the REAL Master Admin login on activate.
  usernameFor = (req) =>
    this.state.adminUserDraft[req.orderNumber] != null
      ? this.state.adminUserDraft[req.orderNumber]
      : req.adminUser || this.defaultUsername(req);

  // The username that will actually be used (typed value, or the default when
  // blank) — also what we check for availability.
  effectiveUsername = (req) =>
    (this.usernameFor(req) || '').trim() || this.defaultUsername(req);

  // Debounced availability check against the backend.
  checkUsername = (req) => {
    const username = this.effectiveUsername(req);
    const key = req.orderNumber;
    this.setState((s) => ({
      usernameStatus: {
        ...s.usernameStatus,
        [key]: { state: 'checking', value: username },
      },
    }));
    clearTimeout(this._userTimers[key]);
    this._userTimers[key] = setTimeout(() => {
      AuthUtil.postRequest(ONBOARDING.CHECK_USERNAME, { username })
        .then((res) => {
          // Ignore stale responses if the field changed again meanwhile.
          if (this.effectiveUsername(req) !== username) return;
          const available = !!(res && res.data && res.data.available);
          this.setState((s) => ({
            usernameStatus: {
              ...s.usernameStatus,
              [key]: { state: available ? 'available' : 'taken', value: username },
            },
          }));
        })
        .catch(() => {
          // On a transient error don't hard-block — let the backend re-check
          // on activate (it still guards uniqueness there).
          this.setState((s) => ({
            usernameStatus: {
              ...s.usernameStatus,
              [key]: { state: 'available', value: username },
            },
          }));
        });
    }, 400);
  };

  passwordFor = (req) =>
    this.state.passwordDraft[req.orderNumber] != null
      ? this.state.passwordDraft[req.orderNumber]
      : req.masterAdminPassword || this.tempPassword(req);

  passwordModeFor = (req) => this.state.passwordMode[req.orderNumber] || 'auto';

  setPasswordMode = (req, mode) =>
    this.setState((s) => {
      const next = {
        passwordMode: { ...s.passwordMode, [req.orderNumber]: mode },
      };
      // Switching to auto seeds a freshly generated strong password.
      if (mode === 'auto') {
        next.passwordDraft = {
          ...s.passwordDraft,
          [req.orderNumber]: generatePassword(),
        };
      }
      return next;
    });

  regeneratePassword = (req) =>
    this.setState((s) => ({
      passwordDraft: {
        ...s.passwordDraft,
        [req.orderNumber]: generatePassword(),
      },
    }));

  // Fetch the branded invoice PDF (base64) and hand the bytes up to the parent
  // (ManageCompanyTable), which renders the preview + toolbar (Back / Download /
  // Print / Zoom in the contextual menu) exactly like Master Reading.
  viewInvoice = (req) => {
    if (this.state.invoiceLoading) return;
    this.setState({ invoiceLoading: req.orderNumber });
    AuthUtil.postRequest(ONBOARDING.INVOICE, { orderNumber: req.orderNumber })
      .then((res) => {
        const data = res && res.data;
        if (data && data.success && data.pdfBase64 && this.props.onShowInvoice) {
          this.props.onShowInvoice(req.orderNumber, base64ToUint8(data.pdfBase64));
        }
        this.setState({ invoiceLoading: null });
      })
      .catch(() => this.setState({ invoiceLoading: null }));
  };

  // Temporary master-admin password mirrors the backend convention:
  // company name, lowercased, spaces removed.
  tempPassword = (req) => (req.company || '').toLowerCase().replace(/\s+/g, '');

  toggleExpand = (req) => {
    const opening = this.state.expanded !== req.orderNumber;
    this.setState((s) => {
      const next = { expanded: opening ? req.orderNumber : null };
      // Seed an auto-generated temp password the first time the card opens
      // (default mode is Auto-generate).
      if (
        opening &&
        req.status !== 'Completed' &&
        s.passwordDraft[req.orderNumber] == null &&
        !req.masterAdminPassword
      ) {
        next.passwordDraft = {
          ...s.passwordDraft,
          [req.orderNumber]: generatePassword(),
        };
      }
      return next;
    });
    // Kick off the username availability check the first time a card is opened
    // so the Activate button reflects whether the login is free.
    if (opening && req.status !== 'Completed') this.checkUsername(req);
  };

  toggleStep = (req, key) => {
    if (req.status === 'Completed') return;
    const steps = { ...(req.steps || {}) };
    steps[key] = !steps[key];
    this.props.onUpdate({ orderNumber: req.orderNumber, steps });
  };

  startSetup = (req) =>
    this.props.onUpdate({ orderNumber: req.orderNumber, status: 'In Progress' });

  saveAdminUser = (req) => {
    const value = this.state.adminUserDraft[req.orderNumber];
    if (value != null && value !== req.adminUser) {
      this.props.onUpdate({ orderNumber: req.orderNumber, adminUser: value });
    }
  };

  requestWelcome = (orderNumber) =>
    this.setState({ confirmWelcome: orderNumber });

  cancelWelcome = () => this.setState({ confirmWelcome: null });

  confirmSendWelcome = (req) => {
    this.setState({ sendingWelcome: req.orderNumber, confirmWelcome: null });
    this.props.onSendWelcome({ orderNumber: req.orderNumber }, (success, response) => {
      this.setState((s) => ({
        sendingWelcome: null,
        welcomeSent: success
          ? { ...s.welcomeSent, [req.orderNumber]: response?.to || req.email }
          : s.welcomeSent,
      }));
    });
  };

  // Build a CompanyWizardV3-style create payload from the order, then activate.
  markCompleted = (req) => {
    const planId =
      req.planId && PLANS[req.planId] ? req.planId : inferPlanIdFromName(req.plan);
    const plan = PLANS[planId];
    // Use the add-ons + duration the customer actually purchased at sign-up.
    const addOnIds = Array.isArray(req.addOns) ? req.addOns : [];
    const durationId = req.durationId || '1';
    const months = Number(durationId) || 1; // durationId === months
    const dates = computeTrialDates(new Date(), months);
    const pricing = computePricing({ planId, addOnIds, durationId });
    const authorities = computeAuthorities(planId, addOnIds);
    const licenseKey = generateLicenseKeyHex();

    // The technician's typed username/password become the actual Master Admin
    // login (backend auto-generates only when these are left blank).
    const masterAdminUsername = (this.usernameFor(req) || '').trim();
    const masterAdminPassword = (this.passwordFor(req) || '').trim();

    const payload = {
      name: req.company,
      masterAdminUsername,
      masterAdminPassword,
      contactName: req.owner,
      phone: req.phone,
      addressLine1: req.address || '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'United States',
      linkedCompanies: [],
      planName: plan.name,
      planId,
      compType: COMP_TYPE.GLOBAL,
      addOns: addOnIds,
      licenseDuration: durationId,
      amount: pricing.total,
      authorities,
      licenseKey,
      startDate: dates.startDate.toISOString(),
      trialEnd: dates.trialEnd.toISOString(),
      trialDays: dates.trialDays,
      expiryDate: dates.expiryDate.toISOString(),
      enableCompany: false,
      enabled: false,
      status: 'hold',
      paymentMethod: 'card',
      paymentDetails: {
        source: 'onboarding',
        orderNumber: req.orderNumber,
        total: req.total,
      },
      nfcTicketOutEnabled: true,
    };

    this.setState({ activating: req.orderNumber });
    this.props.onActivate({ orderNumber: req.orderNumber, payload }, (ok) => {
      // On success the order becomes Completed — jump to that chip and keep the
      // card open so the credentials + Send-welcome panel stays in view.
      this.setState(
        ok
          ? { activating: null, filter: 'Completed', expanded: req.orderNumber }
          : { activating: null }
      );
    });
  };

  counters() {
    const r = this.props.requests || [];
    return {
      pending: r.filter((x) => x.status === 'Pending Setup').length,
      progress: r.filter((x) => x.status === 'In Progress').length,
      completed: r.filter((x) => x.status === 'Completed').length,
    };
  }

  renderCard(req) {
    const open = this.state.expanded === req.orderNumber;
    const st = STATUS[req.status] || { bg: '#eee', fg: '#555', accent: '#9aa6bd' };
    const steps = req.steps || {};
    const isCompleted = req.status === 'Completed';
    const isActivating = this.state.activating === req.orderNumber;
    const adminDraft = this.usernameFor(req);
    const passwordDraft = this.passwordFor(req);
    const pwMode = this.passwordModeFor(req);
    const uStatus = this.state.usernameStatus[req.orderNumber];
    const usernameTaken = uStatus && uStatus.state === 'taken';
    const usernameOk = uStatus ? uStatus.state === 'available' : false;

    return (
      <div
        key={req.orderNumber}
        style={{
          background: '#fff',
          border: '1px solid #E5EAF4',
          borderLeft: `4px solid ${st.accent}`,
          borderRadius: 14,
          padding: '18px 20px',
          marginBottom: 16,
          boxShadow: '0 10px 30px -22px rgba(10,22,64,0.45)',
        }}
      >
        {/* header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 19, fontWeight: 800, color: '#0A1640' }}>
              {req.company}
            </div>
            <div style={{ fontSize: 13, color: '#5B6781', marginTop: 3 }}>
              {req.orderNumber} · {req.plan} Plan ·{' '}
              <strong style={{ color: '#0A1640' }}>
                ${req.chargedTotal || req.total}
              </strong>{' '}
              {req.pay}
              {req.feeTotal ? (
                <span style={{ color: '#9aa6bd' }}>
                  {' '}
                  (${req.total} + ${req.feeTotal} fees)
                </span>
              ) : null}
            </div>
          </div>
          <span
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.03em',
              padding: '6px 12px',
              borderRadius: 20,
              whiteSpace: 'nowrap',
              background: st.bg,
              color: st.fg,
            }}
          >
            {req.status}
          </span>
        </div>

        {/* info */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px 24px',
            margin: '16px 0 4px',
          }}
        >
          <Field k="Owner" v={req.owner || '—'} />
          <Field k="Phone" v={req.phone || '—'} />
          <Field k="Email" v={req.email || '—'} />
          <Field k="Preferred contact" v={req.contact || '—'} />
          <Field k="Billing" v={billingText(req)} />
          <Field k="Add-ons" v={addOnsText(req)} />
          <Field k="Address" v={req.address || '—'} span />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            marginTop: 6,
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="text"
            size="small"
            onClick={() => this.toggleExpand(req)}
            endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            style={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#1E3FD0',
              paddingLeft: 0,
            }}
          >
            {open ? 'Hide setup' : 'Open & configure'}
          </Button>
          <Button
            variant="text"
            size="small"
            disabled={this.state.invoiceLoading === req.orderNumber}
            onClick={() => this.viewInvoice(req)}
            startIcon={<InvoiceIcon style={{ fontSize: 18 }} />}
            style={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#5B6781',
            }}
          >
            {this.state.invoiceLoading === req.orderNumber
              ? 'Opening…'
              : 'View invoice'}
          </Button>
        </div>

        <Collapse in={open}>
          <div
            style={{
              borderTop: '1px solid #E5EAF4',
              marginTop: 12,
              paddingTop: 16,
            }}
          >
            <SectionLabel>Technician checklist</SectionLabel>
            {CHECKLIST.map((c) => (
              <div
                key={c.key}
                onClick={() => this.toggleStep(req, c.key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 11,
                  padding: '8px 0',
                  fontSize: 14,
                  color: '#0A1640',
                  cursor: isCompleted ? 'default' : 'pointer',
                }}
              >
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 6,
                    display: 'grid',
                    placeItems: 'center',
                    color: '#fff',
                    flexShrink: 0,
                    background: steps[c.key] ? '#16A34A' : 'transparent',
                    border: `2px solid ${steps[c.key] ? '#16A34A' : '#c5cdda'}`,
                  }}
                >
                  {steps[c.key] && <CheckIcon style={{ fontSize: 14 }} />}
                </span>
                {c.label}
              </div>
            ))}

            <div style={{ margin: '14px 0 22px' }}>
              <SectionLabel>Master Admin login</SectionLabel>
              <TextField
                style={{ marginTop: '16px' }}
                fullWidth
                size="small"
                variant="outlined"
                label="Username"
                placeholder="e.g. master@redangel.com"
                value={adminDraft}
                disabled={isCompleted}
                error={!isCompleted && usernameTaken}
                helperText={
                  isCompleted
                    ? ' '
                    : !uStatus
                    ? ' '
                    : uStatus.state === 'checking'
                    ? 'Checking availability…'
                    : uStatus.state === 'taken'
                    ? 'This username is already taken — choose another.'
                    : 'Username is available.'
                }
                FormHelperTextProps={
                  uStatus && uStatus.state === 'available'
                    ? { style: { color: '#16A34A' } }
                    : undefined
                }
                onChange={(e) => {
                  // Capture the value before the async setState updater runs —
                  // React 16 recycles the synthetic event, so reading
                  // e.target.value inside the updater throws (null target).
                  const value = e.target.value;
                  this.setState(
                    (s) => ({
                      adminUserDraft: {
                        ...s.adminUserDraft,
                        [req.orderNumber]: value,
                      },
                    }),
                    () => this.checkUsername(req)
                  );
                }}
                onBlur={() => this.saveAdminUser(req)}
              />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  margin: '20px 0 10px',
                }}
              >
                <span
                  style={{ fontSize: 13, fontWeight: 700, color: '#5B6781' }}
                >
                  Temporary password
                </span>
                <span style={{ flex: 1 }} />
                <ModeToggle
                  active={pwMode === 'manual'}
                  disabled={isCompleted}
                  onClick={() => this.setPasswordMode(req, 'manual')}
                >
                  Enter manually
                </ModeToggle>
                <ModeToggle
                  active={pwMode === 'auto'}
                  disabled={isCompleted}
                  onClick={() => this.setPasswordMode(req, 'auto')}
                >
                  Auto-generate
                </ModeToggle>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={passwordDraft}
                  disabled={isCompleted}
                  InputProps={
                    pwMode === 'auto'
                      ? { readOnly: true, style: { fontFamily: 'monospace' } }
                      : undefined
                  }
                  helperText={
                    pwMode === 'auto'
                      ? 'Auto-generated — use the refresh icon for a new one.'
                      : 'Enter the password the customer will use to sign in.'
                  }
                  onChange={(e) => {
                    if (pwMode === 'auto') return;
                    const value = e.target.value;
                    this.setState((s) => ({
                      passwordDraft: {
                        ...s.passwordDraft,
                        [req.orderNumber]: value,
                      },
                    }));
                  }}
                />
                {pwMode === 'auto' && !isCompleted && (
                  <Tooltip title="Generate a new password">
                    <IconButton
                      size="small"
                      onClick={() => this.regeneratePassword(req)}
                      style={{ marginBottom: 18 }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </div>

              <div style={{ fontSize: 11.5, color: '#9aa6bd', marginTop: 4 }}>
                Defaults to <code>admin@{'{company}'}.com</code> and an
                auto‑generated password — edit either before activating. This
                exact username &amp; password become the Master Admin login.
              </div>
            </div>

            {!isCompleted && (
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  flexWrap: 'wrap',
                  marginTop: 14,
                }}
              >
                {req.status === 'Pending Setup' && (
                  <Button
                    variant="outlined"
                    onClick={() => this.startSetup(req)}
                    style={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Start Setup
                  </Button>
                )}
                <Tooltip
                  title={
                    usernameTaken
                      ? 'That Master Admin username is already taken'
                      : !usernameOk
                      ? 'Checking username availability…'
                      : ''
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      disabled={isActivating || !usernameOk}
                      onClick={() => this.markCompleted(req)}
                      style={{
                        textTransform: 'none',
                        fontWeight: 700,
                        background:
                          isActivating || !usernameOk ? '#9aa6bd' : '#C99700',
                        color: '#fff',
                      }}
                    >
                      {isActivating ? 'Activating…' : 'Mark Completed & Activate'}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            )}

            {isCompleted && this.renderCredentials(req)}
          </div>
        </Collapse>
      </div>
    );
  }

  renderCredentials(req) {
    // "Sent" persists via the order's welcomeSentAt (survives reloads); the
    // session map covers the brief gap before the list refresh lands.
    const sessionSentTo = this.state.welcomeSent[req.orderNumber];
    const sentWhenMs = req.welcomeSentAt;
    const isSent = !!(sentWhenMs || sessionSentTo);
    const sentEmail = sessionSentTo || req.email;
    return (
      <div style={{ marginTop: 12 }}>
        <SectionLabel>Account credentials — share with the customer</SectionLabel>
        <div
          style={{
            background: '#F4F6FB',
            border: '1px solid #E5EAF4',
            borderRadius: 11,
            padding: '12px 14px',
            fontSize: 13.5,
            color: '#0A1640',
            lineHeight: 1.8,
          }}
        >
          <div>
            Username: <strong>{req.adminUser || '—'}</strong>
          </div>
          <div>
            Temporary password:{' '}
            <strong>{req.masterAdminPassword || this.tempPassword(req) || '—'}</strong>
          </div>
          <div>
            License key:{' '}
            <strong style={{ fontFamily: 'monospace', letterSpacing: '0.08em' }}>
              {req.licenseKey || '—'}
            </strong>
          </div>
        </div>

        {this.state.confirmWelcome === req.orderNumber ? (
          <div
            style={{
              marginTop: 12,
              background: '#FFFBEC',
              border: '1px solid #FCE8A6',
              borderRadius: 11,
              padding: '12px 14px',
            }}
          >
            <div style={{ fontSize: 13.5, color: '#0A1640', marginBottom: 10 }}>
              Send the welcome email (login credentials + license key) to{' '}
              <strong>{req.email}</strong>?
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button
                size="small"
                onClick={this.cancelWelcome}
                style={{ textTransform: 'none', fontWeight: 700 }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => this.confirmSendWelcome(req)}
                style={{
                  textTransform: 'none',
                  fontWeight: 700,
                  background: '#1E3FD0',
                  color: '#fff',
                }}
              >
                Yes, send email
              </Button>
            </div>
          </div>
        ) : isSent ? (
          <div
            style={{
              marginTop: 12,
              background: '#DCFCE7',
              border: '1px solid #86efac',
              borderRadius: 11,
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: 13, color: '#15803d', fontWeight: 700 }}>
              <span role="img" aria-label="done">
                ✅
              </span>{' '}
              Welcome email sent to {sentEmail}
              {sentWhenMs ? ` on ${formatSentTime(sentWhenMs)}` : ''}.
            </span>
            <span style={{ flex: 1 }} />
            <Button
              size="small"
              variant="outlined"
              disabled={this.state.sendingWelcome === req.orderNumber}
              onClick={() => this.requestWelcome(req.orderNumber)}
              style={{ textTransform: 'none', fontWeight: 700 }}
            >
              {this.state.sendingWelcome === req.orderNumber
                ? 'Sending…'
                : 'Send again'}
            </Button>
          </div>
        ) : (
          <Button
            variant="outlined"
            disabled={this.state.sendingWelcome === req.orderNumber}
            onClick={() => this.requestWelcome(req.orderNumber)}
            style={{ textTransform: 'none', fontWeight: 700, marginTop: 12 }}
          >
            {this.state.sendingWelcome === req.orderNumber
              ? 'Sending…'
              : 'Send welcome email to customer'}
          </Button>
        )}
        <div style={{ fontSize: 11.5, color: '#9aa6bd', marginTop: 8 }}>
          No email is sent automatically — use this once the account is ready.
        </div>
      </div>
    );
  }

  render() {
    const c = this.counters();
    const list = this.props.requests || [];
    const { filter } = this.state;
    const filtered = list.filter((r) => r.status === filter);

    const FILTERS = [
      { label: 'Pending', value: 'Pending Setup', count: c.pending, accent: '#E7B600' },
      { label: 'In Progress', value: 'In Progress', count: c.progress, accent: '#1E3FD0' },
      { label: 'Completed', value: 'Completed', count: c.completed, accent: '#16A34A' },
    ];

    return (
      <div style={{ padding: '12px 24px 32px' }}>
        <div style={{ display: 'flex', gap: 10, margin: '4px 0 18px', flexWrap: 'wrap' }}>
          {FILTERS.map((f) => (
            <FilterChip
              key={f.value}
              label={f.label}
              count={f.count}
              accent={f.accent}
              active={filter === f.value}
              onClick={() => this.setFilter(f.value)}
            />
          ))}
        </div>

        {filtered.length === 0 ? (
          <OnboardingEmpty filter={filter} />
        ) : (
          filtered.map((req) => this.renderCard(req))
        )}
      </div>
    );
  }
}

function billingText(req) {
  const monthly = req.monthly || req.total || '—';
  const dur = DURATION_BY_ID[req.durationId || '1'];
  const term = dur && dur.months > 1 ? ` · ${dur.label}` : '';
  return `$${monthly}/mo${term}`;
}

function addOnsText(req) {
  const ids = Array.isArray(req.addOns) ? req.addOns : [];
  if (!ids.length) return 'None';
  return ids.map((id) => (ADD_ON_BY_ID[id] ? ADD_ON_BY_ID[id].name : id)).join(', ');
}

// Strong, human-friendly temp password — no ambiguous chars (0/O/1/l/I).
function generatePassword() {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnpqrstuvwxyz';
  const digits = '23456789';
  const all = upper + lower + digits;
  const pick = (set) => set[Math.floor(Math.random() * set.length)];
  // Guarantee at least one of each class, then fill to 10 chars.
  let out = pick(upper) + pick(lower) + pick(digits) + pick(digits);
  for (let i = out.length; i < 10; i += 1) out += pick(all);
  // Shuffle so the guaranteed chars aren't always in front.
  return out
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('');
}

function ModeToggle({ active, disabled, onClick, children }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        border: `1.5px solid ${active ? '#1E3FD0' : '#d6deec'}`,
        background: active ? '#EEF3FF' : '#fff',
        color: active ? '#1E3FD0' : '#5B6781',
        fontWeight: 700,
        fontSize: 12,
        padding: '5px 12px',
        borderRadius: 8,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}

function formatSentTime(ms) {
  try {
    return new Date(ms).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (e) {
    return '';
  }
}

function base64ToUint8(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: '#9aa6bd',
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function Field({ k, v, span }) {
  return (
    <div style={span ? { gridColumn: '1 / -1' } : null}>
      <div
        style={{
          color: '#9aa6bd',
          fontWeight: 700,
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {k}
      </div>
      <div style={{ color: '#0A1640', fontWeight: 600, fontSize: 13.5 }}>{v}</div>
    </div>
  );
}

function OnboardingEmpty({ filter }) {
  const MAP = {
    'Pending Setup': {
      title: 'No requests pending setup',
      sub: 'Paid sign-ups from the landing page land here automatically, ready for a technician to configure.',
    },
    'In Progress': {
      title: 'Nothing in progress',
      sub: 'Requests you’ve started setting up appear here until you activate them.',
    },
    Completed: {
      title: 'No completed requests yet',
      sub: 'Once you activate a request, the new company and its credentials show up here.',
    },
  };
  const m = MAP[filter] || { title: 'No requests', sub: '' };
  return (
    <div
      style={{
        background: '#fff',
        border: '1px dashed #d6deec',
        borderRadius: 16,
        padding: '52px 32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: '#F4F6FB',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <InboxIcon style={{ fontSize: 32, color: '#9aa6bd' }} />
      </div>
      <div
        style={{
          fontSize: 17,
          fontWeight: 800,
          color: '#0A1640',
          marginTop: 16,
        }}
      >
        {m.title}
      </div>
      <div
        style={{
          fontSize: 13.5,
          color: '#5B6781',
          marginTop: 6,
          maxWidth: 420,
          lineHeight: 1.5,
        }}
      >
        {m.sub}
      </div>
    </div>
  );
}

function FilterChip({ label, count, accent, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        border: `1.5px solid ${active ? accent : '#E5EAF4'}`,
        background: active ? `${accent}14` : '#fff',
        color: active ? '#0A1640' : '#5B6781',
        fontWeight: 700,
        fontSize: 13.5,
        padding: '8px 14px',
        borderRadius: 999,
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          width: 9,
          height: 9,
          borderRadius: '50%',
          background: accent,
          display: 'inline-block',
        }}
      />
      {label}
      <span
        style={{
          minWidth: 20,
          textAlign: 'center',
          background: active ? accent : '#EEF1F7',
          color: active ? '#fff' : '#5B6781',
          fontSize: 12,
          fontWeight: 800,
          borderRadius: 999,
          padding: '1px 7px',
        }}
      >
        {count}
      </span>
    </button>
  );
}

export default OnboardingRequests;
